// Login screen — ported from legacy v20_Index.html: markup (lines 468-483),
// the login-form submit handler (~1121-1128), showLoginErr (~1137) and
// retryAccess (~1140). The access-gate DATA semantics live in auth.js
// (verifyAccess); this view renders the three outcomes:
//   retry — "Connection problem … Tap to retry" (session KEPT, F-AUTH-04);
//   deny  — "Access denied. Contact your administrator." (already torn down);
//   ok    — hand off to the shell via the onSuccess callback.

import { esc } from '../ui/esc.js';
import { register } from '../ui/actions.js';
import { announce, cancelAnnounce } from '../ui/announce.js';
import { getClient } from '../db/client.js';
import { netErr } from '../errors.js';
import { verifyAccess, getPendingAccessUser, teardownSession, ACCESS_DENIED_MSG } from '../auth.js';
import { evaluateLoginMfa, blocksLogin, verifyCode, isValidCode, MFA_GATE } from '../data/mfa.js';

/**
 * Pure: user-facing copy for a signInWithPassword error. Genuine auth
 * responses ('Invalid login credentials', …) keep their legacy verbatim
 * wording, but NETWORK-shaped failures (AuthRetryableFetchError surfaces the
 * raw fetch internals: 'Failed to fetch' / 'Load failed') route through
 * netErr() so the nurse gets the friendly no-connection copy plus a TU- ref
 * logged in the ring buffer for support.
 * @param {any} error supabase auth error
 * @returns {string}
 */
export function loginErrCopy(error) {
  const msg = (error && error.message) || '';
  const netShaped =
    (error && error.name === 'AuthRetryableFetchError') ||
    /fetch|network|load failed|timeout/i.test(msg); // same classifier as errors.netErr
  return netShaped ? netErr(error, 'Could not sign in') : msg;
}

/** @type {(() => void)|null} */
let _onSuccess = null;
/**
 * Latch: has THIS login screen already handed off to the shell? (review R-11)
 * boot() restores a stored session through startAccess() while the form is
 * already live, so a nurse who signs in during that window sends a second flow
 * through the same gate. Both used to reach onSuccess — two mountShell(), two
 * router listeners, duplicate loaders/realtime/idle timers. Only a PASSED gate
 * sets it (a 'retry' outcome has not entered anything); showLogin() re-arms it.
 */
let _entered = false;

/** @returns {any} the login error region (or null before render) */
function errEl() {
  const doc = /** @type {any} */ (globalThis).document;
  return doc ? doc.getElementById('lerr') : null;
}

/**
 * Show an inline login error (legacy showLoginErr — textContent, never HTML).
 * @param {string} m
 * @returns {void}
 */
export function showLoginErr(m) {
  const el = errEl();
  if (!el) return;
  // VISIBLE FIRST, THEN THE TEXT — in two different tasks. `.login-err` is
  // display:none until `.show`, and a role="alert" mutation made outside the
  // render tree is not announced. Reordering alone left both statements in one
  // synchronous block, so the accessibility pass still met the region with its
  // text already in place; announce() puts a real gap between them (see
  // ui/announce.js). It also clears the region first, so the SECOND wrong
  // password — the same string as the first — is a real mutation rather than
  // a no-op the screen reader never hears.
  el.classList.add('show');
  announce(el, m);
}

/** Hide the error region and EMPTY it (legacy only removed `.show`, leaving
 *  stale error text in the document indefinitely). @returns {void} */
function hideLoginErr() {
  const el = errEl();
  if (!el) return;
  el.classList.remove('show');
  // Drop a deferred write still in flight, or it repopulates a hidden region.
  cancelAnnounce(el);
  el.textContent = '';
}

/**
 * The network-failure branch UI: keeps the session, offers a retry that
 * re-verifies WITHOUT re-entering the password. Markup port of legacy
 * handleAccess's error branch (inline style moved to CSS; onclick replaced
 * by data-action per CSP).
 * @returns {void}
 */
function showRetryUI() {
  const el = errEl();
  if (!el) return;
  el.innerHTML =
    'Connection problem — could not verify your account. ' +
    '<button type="button" class="btn sm retry-btn" data-action="login-retry">Tap to retry</button>';
  el.classList.add('show');
}

/**
 * Run the access gate for an authenticated user and render the outcome.
 * (Legacy handleAccess drove the same #lerr region directly.)
 * @param {any} user supabase auth user
 * @returns {Promise<void>}
 */
export async function attemptAccess(user) {
  const res = await verifyAccess(user);
  if (res.status === 'retry') showRetryUI();
  else if (res.status === 'deny') showLoginErr(ACCESS_DENIED_MSG);
  else if (_onSuccess && !_entered) {
    _entered = true;
    _onSuccess();
  }
}

// ─── R-021: the aal2 challenge, between sign-in and the access gate ────────
//
// ORDER MATTERS. The code is asked for BEFORE verifyAccess() reads app_users,
// because if this project ever gates its RLS policies on aal2 then that read
// would fail for an aal1 session — and classifyAccess() correctly turns a
// failed read into 'retry', so the nurse would get "Connection problem" for
// what is actually "we need your code". Challenge first, then the gate.
//
// RE-ENTRANCY. Every path through here re-renders the panel from scratch and
// leaves the auth session exactly as Supabase left it. A wrong code re-prompts;
// a network failure re-prompts with a different message; NOTHING here signs the
// user out behind their back. The one action that ends the session is the
// escape hatch the user clicks themselves.

/** @type {any} */
let _mfaUser = null;
/** @type {string|null} */
let _mfaFactorId = null;

/** Test-only: clear the challenge stash. @returns {void} */
export function _resetMfaChallenge() {
  _mfaUser = null;
  _mfaFactorId = null;
}

/**
 * Pure: the user-facing message for a failed code attempt. Kept pure so the
 * "a wrong code is a retry, not a lockout" promise is testable.
 * @param {{code?: string|null, error?: any}} res the verifyCode() envelope
 * @returns {{msg: string, retry: boolean}}
 */
export function codeErrCopy(res) {
  if (!res) return { msg: 'Something went wrong. Try the code again.', retry: true };
  if (res.code === 'MFA_BAD_CODE') {
    return { msg: 'Enter the 6-digit code from your authenticator app.', retry: true };
  }
  const m = String((res.error && res.error.message) || '');
  if (/fetch|network|load failed|timeout/i.test(m)) {
    return { msg: netErr(res.error, 'Could not check the code'), retry: true };
  }
  // Supabase's own wording for a bad/expired code. Say what to do about it:
  // the commonest real cause is a phone clock that has drifted.
  return {
    msg:
      'That code was not accepted. Codes change every 30 seconds — wait for the next one and try again. ' +
      'If it keeps failing, check that your phone’s clock is set automatically.',
    retry: true,
  };
}

/** Pure: the brand block every login panel opens with. @returns {string} */
function brandHtml() {
  return (
    '<div class="login-brand"><span class="wm login-wm"><span class="b">Insights</span><span class="r">IQ</span></span>' +
    '<p class="login-sub">Tumaini Patient Support Programme</p></div>'
  );
}

/**
 * Pure: THE ESCAPE HATCH. Always present, on every post-password panel, in
 * every error state. The failure this prevents is a half-authenticated screen
 * whose only remaining option is to close the tab and hope.
 * @returns {string}
 */
function escapeHatchHtml() {
  return (
    '<p class="login-mfa-out">Phone not to hand, or something looks wrong? ' +
    '<button type="button" class="btn sm ghost" data-action="mfa-signout">Sign out and start again</button></p>'
  );
}

/**
 * Pure: the panel shown between a CORRECT code and the access-gate outcome.
 *
 * It MUST carry the `lerr` region. attemptAccess() renders both of its
 * non-success outcomes there — 'retry' (F-AUTH-04, "Tap to retry" WITHOUT
 * re-entering the password) and 'deny' (ACCESS_DENIED_MSG). The challenge
 * panel has only `mfa-err`, so leaving it on screen after a correct code left
 * a verified user staring at a frozen prompt with a disabled "Checking…"
 * button and no message at all — the exact dead end the RE-ENTRANCY note
 * above promises cannot happen.
 * @returns {string}
 */
export function verifyingPanelHtml() {
  return (
    brandHtml() +
    '<h2 class="login-h">Verifying your account</h2>' +
    '<p class="login-mfa-lede">Code accepted. Checking your access…</p>' +
    '<div class="login-err" id="lerr" role="alert"></div>' +
    escapeHatchHtml()
  );
}

/** Swap the card to the verifying panel so the outcome has somewhere to land. */
function showVerifying() {
  const doc = /** @type {any} */ (globalThis).document;
  const card = doc && doc.querySelector('.login-card');
  if (card) card.innerHTML = verifyingPanelHtml();
}

/** Render the challenge panel into the login card. Re-entrant by construction. */
function showChallenge(msg) {
  const doc = /** @type {any} */ (globalThis).document;
  const card = doc && doc.querySelector('.login-card');
  if (!card) return;
  card.innerHTML =
    brandHtml() +
    '<h2 class="login-h">Enter your code</h2>' +
    '<p class="login-mfa-lede">Open your authenticator app and type the 6-digit code for Tumaini.</p>' +
    '<form class="login-form" id="mfa-form">' +
    '<div class="field"><label for="mfa-code">6-digit code</label>' +
    '<input id="mfa-code" class="login-mfa-code" type="text" inputmode="numeric" autocomplete="one-time-code" ' +
    'maxlength="7" required placeholder="123456" aria-describedby="mfa-err"/></div>' +
    '<div class="login-err" id="mfa-err" role="alert"></div>' +
    '<button type="submit" class="login-btn" id="mfa-btn">Verify</button>' +
    '</form>' +
    escapeHatchHtml() +
    '<p class="login-foot login-mfa-help">If you have lost the device, another administrator must remove your ' +
    'two-step verification before you can sign in — Tumaini has no self-service reset.</p>';

  const form = doc.getElementById('mfa-form');
  const input = doc.getElementById('mfa-code');
  if (input && input.focus) input.focus();
  // Reveal, then write on the next exposed tick — the same announce() gap as
  // #lerr and the idle-warning region (this is the third variant of that
  // family the earlier accessibility pass left on a template literal: the
  // class and the text used to land in the one synchronous block that built
  // this whole panel, which is the exact ordering announce() exists to break
  // apart). The panel is a brand-new DOM node on every re-render, so the
  // baked-in `${msg ? ' show' : ''}` and inline text were revealed and
  // written at the same instant the node was created.
  if (msg) {
    const err = doc.getElementById('mfa-err');
    if (err) {
      err.classList.add('show');
      announce(err, msg);
    }
  }
  if (!form) return;
  form.addEventListener('submit', async (/** @type {any} */ e) => {
    e.preventDefault();
    const btn = doc.getElementById('mfa-btn');
    const code = (doc.getElementById('mfa-code') || {}).value || '';
    if (!isValidCode(code)) {
      showChallenge('Enter the 6-digit code from your authenticator app.');
      return;
    }
    btn.textContent = 'Checking…';
    btn.disabled = true;
    let res;
    try {
      res = await verifyCode(_mfaFactorId || '', code);
    } catch (err) {
      res = { ok: false, error: err, code: null, data: null };
    }
    if (res.ok) {
      const u = _mfaUser;
      _resetMfaChallenge();
      // Swap panels BEFORE the gate runs: attemptAccess() renders 'retry' and
      // 'deny' into #lerr, which the challenge panel does not have.
      showVerifying();
      try {
        await attemptAccess(u);
      } catch (err) {
        // verifyAccess() returns an envelope rather than throwing, but a
        // subscriber blowing up inside _onSuccess must still leave a message
        // and the escape hatch, never a silent frozen screen.
        showLoginErr(netErr(err, 'Could not verify your account'));
      }
      return;
    }
    // Failed: re-render the panel (which restores the button, clears the field
    // and keeps the escape hatch) with an honest message. Session untouched.
    showChallenge(codeErrCopy(res).msg);
  });
}

/**
 * Sign in fully: run the MFA gate, then the access gate. Every entry point
 * (password submit AND session restore) goes through here, so a restored aal1
 * session with a verified factor is challenged exactly like a fresh login.
 * @param {any} user supabase auth user
 * @returns {Promise<void>}
 */
export async function startAccess(user) {
  const decision = await evaluateLoginMfa();
  if (blocksLogin(decision)) {
    _mfaUser = user;
    _mfaFactorId = decision.factorId;
    showChallenge('');
    return;
  }
  // UNKNOWN is deliberately NOT blocking (see data/mfa.js rule 2) — but it is
  // also not hidden. The user proceeds and the login region says what happened,
  // so a support call has something to go on.
  if (decision.gate === MFA_GATE.UNKNOWN) {
    showLoginErr('Signed in. (Two-step verification could not be checked just now.)');
  }
  await attemptAccess(user);
}

/** The escape hatch: end the session and return to a clean login screen. */
async function mfaSignOut() {
  _resetMfaChallenge();
  try {
    await teardownSession(getClient());
  } catch {
    /* teardownSession never throws, but the reload must happen regardless */
  }
  const w = /** @type {any} */ (globalThis);
  if (w.location && w.location.reload) w.location.reload();
}

/**
 * Retry the account-verification step after a connection failure, WITHOUT
 * re-entering the password (the auth session is still valid — only the
 * app_users read failed). Exact legacy retryAccess port.
 * @returns {void}
 */
function retryAccess() {
  const el = errEl();
  if (el) el.textContent = 'Verifying…';
  const u = getPendingAccessUser();
  if (u) {
    attemptAccess(u);
    return;
  }
  getClient()
    .auth.getSession()
    .then((/** @type {any} */ { data }) => {
      const s = data && data.session;
      if (s) attemptAccess(s.user);
      else if (el) el.classList.remove('show');
    });
}

/**
 * Render the login screen into `host` and wire the form. Legacy markup with
 * labels associated (for/id) and the error region as role="alert" so denials
 * and connection problems are announced.
 * @param {Element} host
 * @param {{onSuccess: () => void}} opts
 * @returns {void}
 */
export function showLogin(host, opts) {
  _onSuccess = opts.onSuccess;
  _entered = false;
  register('login-retry', retryAccess);
  register('mfa-signout', () => void mfaSignOut());
  host.innerHTML =
    '<div class="login-wrap" id="login-wrap"><div class="login-card">' +
    '<div class="login-brand">' +
    '<span class="wm login-wm"><span class="b">Insights</span><span class="r">IQ</span></span>' +
    '<p class="login-sub">Tumaini Patient Support Programme</p></div>' +
    '<h2 class="login-h">Welcome back</h2>' +
    '<form class="login-form" id="login-form">' +
    '<div class="field"><label for="le">Email</label>' +
    '<input id="le" type="email" required placeholder="nurse@insightsiq.co" autocomplete="email"/></div>' +
    '<div class="field"><label for="lp">Password</label>' +
    '<input id="lp" type="password" required placeholder="..." autocomplete="current-password"/></div>' +
    '<div class="login-err" id="lerr" role="alert"></div>' +
    '<button type="submit" class="login-btn" id="lbtn">Sign In</button>' +
    '</form>' +
    '<p class="login-foot">Secured · Row-level access control active</p>' +
    // APP_VERSION on the login screen too (v20.1 parity, R-019).
    `<p class="login-foot login-ver">${esc(APP_VERSION)}</p>` +
    '</div></div>';

  const doc = /** @type {any} */ (globalThis).document;
  const form = doc.getElementById('login-form');
  if (!form) return;
  form.addEventListener('submit', async (/** @type {any} */ e) => {
    // Legacy submit semantics: busy-latch the button for the whole signIn
    // round-trip, then either surface the auth error or run the access gate.
    // (F-AUTH-01) Contained in try/catch/finally: an unexpected throw (e.g.
    // getClient() when the vendor script never loaded, or a store subscriber
    // blowing up inside attemptAccess) must never leave the button locked at
    // 'Signing in…' with a blank error region.
    e.preventDefault();
    const email = doc.getElementById('le').value.trim();
    const pass = doc.getElementById('lp').value;
    const btn = doc.getElementById('lbtn');
    hideLoginErr();
    btn.textContent = 'Signing in…';
    btn.disabled = true;
    try {
      const { data, error } = await getClient().auth.signInWithPassword({
        email,
        password: pass,
      });
      if (error) showLoginErr(loginErrCopy(error));
      else if (data && data.user) await startAccess(data.user); // MFA gate, then the access gate
    } catch (err) {
      showLoginErr(netErr(err, 'Could not sign in'));
    } finally {
      btn.textContent = 'Sign In';
      btn.disabled = false;
    }
  });
}
